import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Link } from "react-router-dom";

const API = "https://api.vyapakprint.com/api";

const formatCurrency = (value) => {
  const num = Number(value || 0);
  return num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const BusinessWallet = () => {
  const [businessUsers, setBusinessUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [businessSelectedUserId, setBusinessSelectedUserId] = useState("");
  const [businessAmount, setBusinessAmount] = useState("");
  const [businessRemarks, setBusinessRemarks] = useState("");
  const [businessReceiptId, setBusinessReceiptId] = useState("");
  const [transactionType, setTransactionType] = useState("credit");

  const [search, setSearch] = useState("");

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        setError("");

        const response = await axios.get(`${API}/distribution/`);
        const raw = response?.data;

        let users = [];
        if (Array.isArray(raw)) users = raw;
        else if (Array.isArray(raw?.data)) users = raw.data;
        else if (Array.isArray(raw?.distributions)) users = raw.distributions;

        setBusinessUsers(users);
      } catch (err) {
        console.error(err);
        setError("Failed to fetch users.");
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const activeUsers = useMemo(() => {
    return businessUsers.filter(
      (u) => String(u?.status || "").toLowerCase() === "active",
    );
  }, [businessUsers]);

  const filteredUsers = useMemo(() => {
    const q = search.toLowerCase();
    return businessUsers.filter((u) => {
      return (
        String(u?.contactPerson || "")
          .toLowerCase()
          .includes(q) ||
        String(u?.businessName || "")
          .toLowerCase()
          .includes(q) ||
        String(u?.mobile || "")
          .toLowerCase()
          .includes(q) ||
        String(u?.email || "")
          .toLowerCase()
          .includes(q)
      );
    });
  }, [businessUsers, search]);

  const selectedUser = useMemo(() => {
    return businessUsers.find(
      (u) => String(u._id) === String(businessSelectedUserId),
    );
  }, [businessUsers, businessSelectedUserId]);

  const totalWallet = useMemo(() => {
    return businessUsers.reduce((sum, u) => sum + Number(u?.wallet || 0), 0);
  }, [businessUsers]);

  const clearForm = () => {
    setBusinessSelectedUserId("");
    setBusinessAmount("");
    setBusinessRemarks("");
    setBusinessReceiptId("");
    setTransactionType("credit");
  };

  const handleWalletUpdate = async () => {
    try {
      setError("");
      setSuccess("");

      if (!businessSelectedUserId) {
        setError("Please select a user.");
        return;
      }

      const amount = Number(businessAmount);
      if (!amount || amount <= 0) {
        setError("Please enter a valid amount.");
        return;
      }

      const currentWallet = Number(selectedUser?.wallet || 0);

      if (transactionType === "debit" && amount > currentWallet) {
        setError("Insufficient wallet balance for deduction.");
        return;
      }

      setSubmitting(true);

      await axios.put(`${API}/distribution/${businessSelectedUserId}/wallet`, {
        wallet: amount,
        remark: businessRemarks,
        receiptId: businessReceiptId,
        transactionType,
      });

      setBusinessUsers((prev) =>
        prev.map((u) => {
          if (String(u._id) !== String(businessSelectedUserId)) return u;

          const oldWallet = Number(u.wallet || 0);
          const newWallet =
            transactionType === "credit"
              ? oldWallet + amount
              : Math.max(0, oldWallet - amount);

          return { ...u, wallet: newWallet };
        }),
      );

      setSuccess(
        transactionType === "credit"
          ? "Money added successfully."
          : "Money deducted successfully.",
      );

      // do NOT clear form
      // clearForm();
    } catch (err) {
      console.error(err);
      setError(
        err?.response?.data?.message ||
          `Failed to ${transactionType === "credit" ? "add" : "deduct"} money.`,
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8fafc] p-4 md:p-6">
        <div className="mx-auto max-w-7xl animate-pulse space-y-6">
          <div className="h-8 w-64  bg-gray-200" />
          <div className="grid gap-4 md:grid-cols-3">
            <div className="h-24  bg-white shadow-sm" />
            <div className="h-24  bg-white shadow-sm" />
            <div className="h-24  bg-white shadow-sm" />
          </div>
          <div className="grid gap-6 lg:grid-cols-[420px,1fr]">
            <div className="h-[420px]  bg-white shadow-sm" />
            <div className="h-[420px]  bg-white shadow-sm" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc] p-4 md:p-6">
      <div className="mx-auto max-w-7xl space-y-6">
        {/* Alerts */}
        {error && (
          <div className=" border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700 shadow-sm">
            {error}
          </div>
        )}

        {success && (
          <div className=" border border-green-200 bg-green-50 px-4 py-3 text-sm font-medium text-green-700 shadow-sm">
            {success}
          </div>
        )}

        {/* Main layout */}
        <div className="grid gap-6 lg:grid-cols-[420px,1fr]">
          {/* Left panel */}
          <div className=" border border-gray-200 bg-white p-5 shadow-sm">
            {/* Toggle */}
            <div className="mb-5  bg-gray-100 p-1">
              <div className="grid grid-cols-2 gap-1">
                <button
                  type="button"
                  onClick={() => setTransactionType("credit")}
                  className={` px-4 py-2.5 text-sm font-semibold transition ${
                    transactionType === "credit"
                      ? "bg-green-600 text-white shadow-sm"
                      : "bg-transparent text-gray-600 hover:bg-white"
                  }`}
                >
                  Credit
                </button>
                <button
                  type="button"
                  onClick={() => setTransactionType("debit")}
                  className={` px-4 py-2.5 text-sm font-semibold transition ${
                    transactionType === "debit"
                      ? "bg-red-600 text-white shadow-sm"
                      : "bg-transparent text-gray-600 hover:bg-white"
                  }`}
                >
                  Deduct
                </button>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700">
                  Select User
                </label>
                <select
                  value={businessSelectedUserId}
                  onChange={(e) => setBusinessSelectedUserId(e.target.value)}
                  className="w-full  border border-gray-300 bg-white px-4 py-3 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                >
                  <option value="">Choose an active user</option>
                  {activeUsers.map((u) => (
                    <option key={u._id} value={u._id}>
                      {u.businessName} ({u.contactPerson})
                    </option>
                  ))}
                </select>
              </div>

              {selectedUser && (
                <div className=" border border-gray-200 bg-gradient-to-br from-gray-50 to-white p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        {selectedUser.contactPerson}
                      </p>
                      <p className="text-sm text-gray-500">
                        {selectedUser.businessName}
                      </p>
                      <p className="mt-1 text-xs text-gray-400">
                        {selectedUser.mobile || "No mobile"}
                      </p>
                    </div>

                    <span className=" bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
                      Wallet
                    </span>
                  </div>

                  <div className="mt-4  bg-gray-900 px-4 py-3 text-white">
                    <p className="text-xs uppercase tracking-wide text-gray-300">
                      Current Balance
                    </p>
                    <h3 className="mt-1 text-2xl font-bold">
                      ₹{formatCurrency(selectedUser.wallet)}
                    </h3>
                  </div>
                </div>
              )}

              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700">
                  Amount
                </label>
                <input
                  type="number"
                  placeholder={
                    transactionType === "credit"
                      ? "Enter amount to credit"
                      : "Enter amount to deduct"
                  }
                  className="w-full  border border-gray-300 px-4 py-3 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                  value={businessAmount}
                  onChange={(e) => setBusinessAmount(e.target.value)}
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700">
                  Remarks
                </label>
                <input
                  placeholder="Enter remarks"
                  className="w-full  border border-gray-300 px-4 py-3 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                  value={businessRemarks}
                  onChange={(e) => setBusinessRemarks(e.target.value)}
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700">
                  Receipt ID
                </label>
                <input
                  placeholder="Enter receipt/reference id"
                  className="w-full  border border-gray-300 px-4 py-3 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                  value={businessReceiptId}
                  onChange={(e) => setBusinessReceiptId(e.target.value)}
                />
              </div>

              <button
                onClick={handleWalletUpdate}
                disabled={submitting}
                className={`w-full  px-4 py-3 text-sm font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-60 ${
                  transactionType === "credit"
                    ? "bg-green-600 hover:bg-green-700"
                    : "bg-red-600 hover:bg-red-700"
                }`}
              >
                {submitting
                  ? "Processing..."
                  : transactionType === "credit"
                    ? "Credit Wallet"
                    : "Deduct Wallet"}
              </button>
            </div>
          </div>

          {/* Right panel */}
          <div className=" border border-gray-200 bg-white shadow-sm">
            <div className="flex flex-col gap-3 border-b border-gray-200 p-5 md:flex-row md:items-center md:justify-between">
              <div className="w-full ">
                <input
                  placeholder="Search by name, business, mobile, email..."
                  className="w-full  border border-gray-300 px-4 py-3 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>

            <div className="overflow-auto">
              <table className="min-w-full text-sm">
                <thead className="sticky top-0 bg-gray-50">
                  <tr className="border-b border-gray-200 text-gray-600">
                    <th className="px-5 py-4 text-left font-semibold">Name</th>
                    <th className="px-5 py-4 text-left font-semibold">
                      Business
                    </th>
                    <th className="px-5 py-4 text-left font-semibold">
                      Mobile
                    </th>
                    <th className="px-5 py-4 text-left font-semibold">
                      Wallet
                    </th>
                    <th className="px-5 py-4 text-center font-semibold">
                      Ledger
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {filteredUsers.length ? (
                    filteredUsers.map((u) => (
                      <tr
                        key={u._id}
                        className="border-b border-gray-100 transition hover:bg-gray-50"
                      >
                        <td className="px-5 py-4">
                          <div>
                            <p className="font-semibold text-gray-900">
                              {u.contactPerson || "-"}
                            </p>
                            <p className="text-xs text-gray-500">
                              {u.email || "No email"}
                            </p>
                          </div>
                        </td>

                        <td className="px-5 py-4 text-gray-700">
                          {u.businessName || "-"}
                        </td>

                        <td className="px-5 py-4 text-gray-700">
                          {u.mobile || "-"}
                        </td>

                        <td className="px-5 py-4">
                          <span className=" bg-green-50 px-3 py-1.5 text-xs font-semibold text-green-700">
                            ₹{formatCurrency(u.wallet)}
                          </span>
                        </td>

                        <td className="px-5 py-4 text-center">
                          <Link
                            to={`/ledger/${u._id}`}
                            className="inline-flex  bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 transition hover:bg-blue-100"
                          >
                            View Ledger
                          </Link>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="px-5 py-14 text-center">
                        <div className="mx-auto max-w-sm">
                          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center  bg-gray-100 text-xl">
                            🔍
                          </div>
                          <p className="text-sm font-semibold text-gray-900">
                            No users found
                          </p>
                          <p className="mt-1 text-sm text-gray-500">
                            Try changing your search keyword.
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessWallet;
